import { create } from 'zustand';
import { apiGet, apiPost, ApiRequestError } from '@/infrastructure/api-client';
import type { LoginResponse, SafeUser } from '@/contracts/auth';

/**
 * SPEC-002 §55/§56: three explicit bootstrap states (never a plain
 * boolean) so the UI can distinguish "we don't know yet" from "logged
 * out". The raw session token never touches this store — only cookies
 * carry it (AUTH-REQ-009).
 */
export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: SafeUser | null;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: SafeUser) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,

  bootstrap: async () => {
    try {
      const response = await apiGet<{ user: SafeUser }>('/api/auth/me');
      set({ status: 'authenticated', user: response.user });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        set({ status: 'unauthenticated', user: null });
        return;
      }
      // Network/unexpected error: treat as unauthenticated rather than
      // hanging in "unknown" forever (plan §56 bootstrap flow).
      set({ status: 'unauthenticated', user: null });
    }
  },

  login: async (email: string, password: string) => {
    const response = await apiPost<LoginResponse>('/api/auth/login', { email, password });
    set({ status: 'authenticated', user: response.user });
  },

  logout: async () => {
    try {
      await apiPost('/api/auth/logout');
    } finally {
      set({ status: 'unauthenticated', user: null });
    }
  },

  setUser: (user: SafeUser) => set({ user }),
}));
